// src/hooks/assistant/useChartData.ts
/**
 * Chart Data Hook for AI Assistant
 * 
 * Fetches and transforms data for assistant charts based on ChartSpec.
 * Uses the unified data layer for:
 * - Shared fetchers (cache-compatible with dashboard)
 * - Chart query factories (encapsulated orchestration)
 */

import { useQuery } from '@tanstack/react-query';
import type { ChartSpec } from '@/types/assistant';
import { getMetricSpec, getXMetricSpec, getYMetricSpec } from '@/types/assistant';
import { TIME } from '@/constants/app';

// Unified data layer
import { 
  assistantChartKeys, 
  fetchChartData,
  type ChartData,
  type BarData,
  type ScatterData,
  type TreemapData,
  type StatCardDataResult,
  type TimeSeriesData,
} from '@/services/data';

// ─────────────────────────────────────────────────────────────────────────────
// Re-export types for consumers
// ─────────────────────────────────────────────────────────────────────────────

export type { 
  ChartData, 
  BarData, 
  ScatterData, 
  TreemapData, 
  StatCardDataResult, 
  TimeSeriesData,
};

// ─────────────────────────────────────────────────────────────────────────────
// Query Key Builder
// ─────────────────────────────────────────────────────────────────────────────

function buildQueryKey(spec: ChartSpec | null) {
  if (!spec) return assistantChartKeys.chart('null');

  const metricSpec = getMetricSpec(spec);
  const xMetricSpec = getXMetricSpec(spec);
  const yMetricSpec = getYMetricSpec(spec);

  // Create a stable, serializable key
  return [
    ...assistantChartKeys.chart(spec.id ?? 'unknown'),
    spec.type,
    spec.tickers?.slice().sort() ?? [],
    spec.categories?.slice().sort() ?? [],
    spec.timeframe ?? null,
    metricSpec?.key ?? null,
    metricSpec?.transform ?? null,
    metricSpec?.window ?? null,
    xMetricSpec?.key ?? null,
    yMetricSpec?.key ?? null,
  ];
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch chart data for assistant-generated charts.
 * 
 * @param spec - The chart specification from the AI assistant
 * @returns React Query result with typed chart data
 */
export function useChartData(spec: ChartSpec | null) {
  return useQuery({
    queryKey: buildQueryKey(spec),
    queryFn: () => fetchChartData(spec!),
    enabled: !!spec,
    staleTime: 5 * TIME.MINUTE,
    gcTime: 10 * TIME.MINUTE,
  });
}
